/**
 * Rules-data type definitions.
 *
 * Architecture decision 3 ("rules as versioned data, not code"): every value the
 * MCS scheme or UK legislation dictates lives here as data, keyed by jurisdiction
 * and effective date, never hard-coded into the calculation engine. Standards
 * change under us mid-transition (MIS 3005-D is already at v2.0/v3.0 in the same
 * month), so every stored calculation records the ruleset version that produced it.
 */

/** UK nations diverge on noise limits and permitted development. */
export type Jurisdiction = "england" | "wales" | "scotland" | "northern-ireland";

/**
 * How much we trust a value.
 *
 * "verified"    — transcribed from the primary standard/legislation PDF and checked.
 * "provisional" — plausible and useful for development, NOT audit-grade. The engine
 *                 refuses to produce a compliance-mode result from provisional data.
 */
export type Provenance = "verified" | "provisional";

export interface SourceRef {
  /** e.g. "MIS 3005-D:2025" */
  readonly standard: string;
  /** e.g. "2.0" */
  readonly issue: string;
  /** e.g. "Table 1" or "3.4.1(e)" */
  readonly clause: string;
  /** ISO date of the standard issue, e.g. "2025-12-05" */
  readonly issued: string;
  /**
   * Page the clause or table is on, as printed in the document's own footer —
   * not the PDF reader's page counter, which differs whenever a document has
   * unnumbered front matter. Optional because the older rulesets predate it.
   */
  readonly page?: number;
  readonly url?: string;
}

export interface Ruleset<T> {
  readonly id: string;
  /** Semantic version of this ruleset payload — recorded on every calculation. */
  readonly version: string;
  /** Ruleset applies from this date (ISO). */
  readonly effectiveFrom: string;
  /** Ruleset superseded on this date (ISO), if it has been. */
  readonly effectiveTo?: string;
  readonly jurisdictions: readonly Jurisdiction[];
  readonly provenance: Provenance;
  readonly source: SourceRef;
  readonly data: T;
}

/** Selects the ruleset in force for a given date and jurisdiction. */
export function resolveRuleset<T>(
  candidates: readonly Ruleset<T>[],
  opts: { on: string; jurisdiction: Jurisdiction },
): Ruleset<T> {
  const applicable = candidates.filter(
    (r) =>
      r.jurisdictions.includes(opts.jurisdiction) &&
      r.effectiveFrom <= opts.on &&
      (r.effectiveTo === undefined || r.effectiveTo > opts.on),
  );

  if (applicable.length === 0) {
    throw new Error(
      `No ruleset in force for jurisdiction "${opts.jurisdiction}" on ${opts.on}. ` +
        `Candidates: ${candidates.map((c) => `${c.id}@${c.version}`).join(", ") || "(none)"}`,
    );
  }

  // Most recently effective wins when several overlap.
  return applicable.reduce((latest, r) =>
    r.effectiveFrom > latest.effectiveFrom ? r : latest,
  );
}
